import { useState } from "react";
import { motion } from "framer-motion";
import { Target } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { fmt } from "@/lib/writing";

const Ring = ({ today, goal, size }) => {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const pct = Math.min(today / goal, 1);
  return (
    <svg width={size} height={size} className="-rotate-90 shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: "rgb(var(--c-line))" }} strokeWidth="4" />
      <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: pct >= 1 ? "rgb(var(--emerald-400))" : "rgb(var(--amber-500))" }} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={c} initial={false} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 0.8, ease: "easeOut" }} />
    </svg>
  );
};

export const GoalRing = ({ today, goal, onGoal, compact }) => {
  const [v, setV] = useState(goal);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/writing/goal", { daily_goal: Number(v) }); onGoal(data.goal); toast.success(`Daily goal set to ${fmt(data.goal)} words`); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const done = today >= goal;
  return (
    <Popover onOpenChange={(o) => o && setV(goal)}>
      <PopoverTrigger asChild>
        <button className="group shrink-0 flex items-center gap-3 rounded-2xl border border-line bg-panel/70 hover:border-amber-500/40 px-3 py-2 transition-colors" data-testid="goal-ring">
          <motion.div animate={done ? { scale: [1, 1.12, 1] } : {}} transition={{ duration: 0.6 }} className="relative">
            <Ring today={today} goal={goal} size={compact ? 34 : 52} />
            {!compact && <Target className={`absolute inset-0 m-auto w-4 h-4 ${done ? "text-emerald-300" : "text-amber-400"}`} />}
          </motion.div>
          <div className="text-left">
            <p className={`whitespace-nowrap font-code ${compact ? "text-[11px]" : "text-sm"} ${done ? "text-emerald-300" : "text-amber-100"}`} data-testid="goal-today">{fmt(today)} <span className="text-slate-500">/ {fmt(goal)}</span></p>
            {!compact && <p className="text-[11px] text-slate-500">{done ? "Goal reached today" : "words today"}</p>}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 bg-panel border-line">
        <form onSubmit={save} className="space-y-2" data-testid="goal-form">
          <p className="text-sm text-slate-200">Daily word goal</p>
          <p className="text-[11px] text-slate-500">Counts words you add across all works. It starts over at midnight.</p>
          <div className="flex gap-2">
            <Input type="number" min={1} max={100000} required value={v} onChange={(e) => setV(e.target.value)} className="h-9" data-testid="goal-input" />
            <button className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-xs font-semibold px-4" data-testid="goal-save-btn">Save</button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};
